import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrayHelpers, DeleteWarning, Forms, Help, Loading, Modal, SaveButton, SystemText, Table } from 'e4shared';
import { setShowSuccess, toggleFormHelp } from 'actions/ui';
import { DefaultOptions } from 'components/simpleAdmin';
import { apiDelete, apiGet, apiPost, apiPatch } from 'services';
import { handleError } from 'actions/admin';
import PageHeader from 'components/general/pageHeader';

export const CRM_Entities = () => [
    { id: 1, entity: 'Contact', name: SystemText('General/CRM/Entity/Contact') },
    { id: 2, entity: 'Task', name: SystemText('General/CRM/Entity/Task') },
    { id: 3, entity: 'Opportunity', name: SystemText('General/CRM/Entity/Opportunity') },
    { id: 4, entity: 'Lead', name: SystemText('General/CRM/Entity/Lead') },
    { id: 5, entity: 'Event', name: SystemText('General/CRM/Entity/Event') },
];

export const SalesforceFields = ({ history }) => {
    const dispatch = useDispatch();
    const pageSize = 10;

    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState([]);
    const [field, setField] = React.useState({});
    const [entities, setEntities] = React.useState(CRM_Entities());
    const [errors, setErrors] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const [show, setShow] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [deleting, setDeleting] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const [sorter, setSorter] = React.useState('name');
    const [desc, setDesc] = React.useState(false);
    const [list, setList] = React.useState('');
    const [entityFields, setEntityFields] = React.useState([]);
    const [lookupError, setLookupError] = React.useState('');

    const user = useSelector(state => state.user.user);

    if (!user.sys_Config) history.replace('/');

    React.useEffect(() => {
        api.load();
    }, []);

    React.useEffect(() => {
        setErrors({});
    }, [show]);

    React.useEffect(() => {
        if (field.crmType) {
            const entity = entities.find(e => e.id === field.crmType);
            if (entity?.properties) {
                setEntityFields(
                    entity.properties
                        .map(p => ({
                            ...p,
                            id: p.name,
                            name: p.label,
                            extraNewLine: p.name,
                        }))
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                );
            } else {
                setEntityFields([]);
            }
        }
    }, [field.crmType]);

    React.useEffect(() => {
        setErrors({});

        if (field.value_Lookup) {
            api.checkLookup(field.value_Lookup);
        }
    }, [field.value_Lookup]);

    React.useEffect(() => {
        const json = textToJson(list);
        setField({ ...field, value_Mapping: json });
    }, [list]);

    const st = (path, ...rest) => SystemText(`Admin/Integrations/Salesforce/Fields/${path}`, ...rest);

    const clean = field => {
        field.extra_Sum = false;
        field.extra_Date = false;
        field.extra_Email = false;

        if (field.value_Type === '') {
            field.value_Lookup = '';
            field.value_Mapping = '';
        } else if (field.value_Type === 'L') {
            field.value_Lookup = '';
        } else if (field.value_Type === 'Q') {
            field.value_Mapping = '';
        } else if (field.value_Type === 'A') {
            field.value_Mapping = '';
            field.value_Lookup = '';
            field.extra_Sum = true;
        } else if (field.value_Type === 'E') {
            field.value_Mapping = '';
            field.value_Lookup = '';
            field.extra_Email = true;
        } else if (field.value_Type === 'D') {
            field.value_Mapping = '';
            field.value_Lookup = '';
            field.extra_Date = true;
        }

        return field;
    };

    const validate = () => {
        const errs = {};
        let valid = true;

        if (!field.name) {
            errs.name = st('Manage/Fields/Name/Error');
            valid = false;
        }

        if (!field.crmType) {
            errs.crmType = st('Manage/Fields/Name/Entity');
            valid = false;
        }

        if (!field.query) {
            errs.query = st('Manage/Fields/Property/Error');
            valid = false;
        } else {
            const property = entityFields.find(x => x.id === field.query);

            if (field.value_Type === 'D' && !['date', 'datetime'].some(x => x === property.type)) {
                errs.query = st('Manage/Fields/Property/DateError');
                valid = false;
            }
        }

        if (field.value_Type === 'Q') {
            if (!field.value_Lookup) {
                errs.value_Lookup = st('Manage/Fields/ValueType/Error');
                valid = false;
            } else if (lookupError) {
                errs.value_Lookup = st('Manage/Fields/ValueType/Error/General', lookupError);
                valid = false;
            }
        }

        setErrors(errs);
        return valid;
    };

    const api = {
        load: () => {
            setLoading(true);
            apiGet('crm-extra-fields/salesforce', user.guid, response => {
                setData(sort(response.map(x => ({ ...x, cannotDelete: !x.company_Id }))));
                apiGet('crm-entities', user.guid, response => {
                    const allEntities = CRM_Entities()
                        .concat(response.map(x => ({ ...x, entity: x.crm_Name })))
                        .sort((a, b) => (a.name > b.name ? 1 : -1));

                    const bespokeEntities = response.reduce((a, c) => `${a},${c.crm_Name}`, []);

                    apiGet(`salesforce-properties?bespoke=${bespokeEntities}`, user.guid, props => {
                        props.forEach(prop => {
                            const entity = allEntities.find(e => e.entity.toLowerCase() === prop.entity.toLowerCase());
                            entity.properties = entity.properties ? entity.properties.concat([prop]) : [prop];
                        });

                        setEntities(allEntities);
                        setLoading(false);
                    });
                });
            });
        },
        checkLookup: x => {
            if (x) {
                apiGet(
                    `salesforce-test-lookup?e=${x}`,
                    user.guid,
                    response => {
                        if (response !== 'OK') {
                            setLookupError(response);
                        } else {
                            setLookupError('');
                            setErrors({ ...errors, value_Lookup: '' });
                        }
                    },
                    null,
                    true
                );
            }
        },
        add: () => {
            if (validate()) {
                setSaving(true);
                apiPost(
                    'crm-extra-field',
                    clean(field),
                    user.guid,
                    () => {
                        dispatch(setShowSuccess(st('Success')));
                        api.load();
                        setShow(false);
                        setSaving(false);
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            } else {
                setSaving(false);
            }
        },
        update: () => {
            if (validate()) {
                setSaving(true);
                apiPatch(
                    'crm-extra-field',
                    clean(field),
                    user.guid,
                    () => {
                        dispatch(setShowSuccess(st('Success')));
                        api.load();
                        setShow(false);
                        setSaving(false);
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            } else {
                setSaving(false);
            }
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(`crm-extra-field/${id}`, null, user.guid, () => {
                    if (idx + 1 === ids.length) {
                        api.load();
                        callback?.();
                    }
                });
            });
        },
    };

    const deleteItem = confirm => {
        setShowDelete(false);
        setDeleting(null);
        if (confirm) {
            api.remove(deleting, api.load);
        }
    };

    const options = {
        ...DefaultOptions(),
        sortBy: sorter,
        ascending: !desc,
        pageSize,
        recordCount: data.length,
        page: page,
        label: st('Field'),
        showDelete: true,
    };

    const getCrmEntity = x => {
        switch (x) {
            case 1:
                return SystemText('General/CRM/Entity/Contact');
            case 2:
                return SystemText('General/CRM/Entity/Task');
            case 3:
                return SystemText('General/CRM/Entity/Opportunity');
            case 4:
                return SystemText('General/CRM/Entity/Lead');
            default: //case 5:
                return SystemText('General/CRM/Entity/Event');
        }
    };

    const typeValueText = x => {
        switch (x) {
            case 'L':
                return st('Manage/Fields/ValueType/User');
            case 'Q':
                return st('Manage/Fields/ValueType/Query');
            case 'A':
                return st('Manage/Fields/ValueType/Amount');
            case 'D':
                return st('Manage/Fields/ValueType/Date');
            case 'E':
                return st('Manage/Fields/ValueType/Email');
            default:
                return st('Manage/Fields/ValueType/Text');
        }
    };

    const tableColumns = [
        {
            label: st('Table/Field'),
            dataProperty: 'name',
            onClick: d => {
                setField(d);
                setList(jsonToText(d.value_Mapping));
                setShow(true);
            },
        },
        {
            label: st('Table/Entity'),
            dataProperty: 'entity',
            format: (value, row) => (row.crmType < 1000 ? getCrmEntity(row.crmType) : value),
        },
        { label: st('Table/Chart'), dataProperty: 'show_Chart', toggle: true },
        { label: st('Table/Type'), dataProperty: 'value_Type', format: (value, row) => typeValueText(value) },
    ];

    const defaultValues = {
        active: true,
        company_Id: user.company_Id,
        crmType: 1,
        filter_Type: '',
        value_Text: '',
        show_Chart: true,
    };

    const sort = data => {
        const flip = desc ? -1 : 1;
        if (['name'].indexOf(sorter) >= 0) {
            return data.sort((a, b) => (a[sorter].toLowerCase() > b[sorter].toLowerCase() ? Number(flip) : -1 * flip));
        } else {
            return data.sort((a, b) => (a[sorter] > b[sorter] ? Number(flip) : -1 * flip));
        }
    };

    const setValue = (p, x) => {
        const values = { ...field };
        values[p] = x;
        setField(values);
    };

    const jsonToText = json => {
        const obj = JSON.parse(json || '[]');
        const text = obj?.length ? obj.reduce((all, x) => (x ? `${all}${x.value} ${x.label}\n` : ''), '') : '';
        return text;
    };

    const textToJson = text => {
        const arr = text
            .split('\n')
            .map(a => {
                const space = a.indexOf(' ');
                if (space) {
                    const value = a.substr(0, space);
                    const label = a.substr(space + 1, a.length - 1);
                    if (value && label) {
                        return { value, label };
                    }
                }

                return null;
            })
            .filter(x => x);

        return JSON.stringify(arr);
    };

    const addNewButton = () => (
        <div className="">
            <button
                id="btn-back"
                className="btn btn-info"
                onClick={() => {
                    history.push('/admin/metrics');
                }}
            >
                <i className="fa fa-chevron-left mr10" />
                {st('Buttons/Back')}
            </button>
            <button
                id="add-new"
                className="btn btn-success"
                onClick={() => {
                    setField(defaultValues);
                    setShow(true);
                }}
            >
                <i className="fa fa-plus mr10" />
                {st('Buttons/New')}
            </button>
        </div>
    );

    const footer = (
        <div>
            <SaveButton
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={() => (field.id ? api.update() : api.add())}
            >
                {st('Buttons/Save')}
            </SaveButton>
            <button id="cancel-button" className="btn" onClick={() => setShow(false)}>
                {st('Buttons/Cancel')}
            </button>
        </div>
    );

    return (
        <div>
            <PageHeader title={st('Title')} extra={addNewButton} />
            <section className="content mtm10">
                <Modal
                    id="add-new-modal"
                    show={show}
                    title={st('Manage/Title')}
                    footer={footer}
                    close={() => setShow(false)}
                    toggleFormHelp={() => dispatch(toggleFormHelp())}
                >
                    <Forms.Text
                        label={st('Manage/Fields/Name/Label')}
                        value={field.name}
                        error={errors.name}
                        placeholder={st('Manage/Fields/Name/Placeholder')}
                        onChange={x => setValue('name', x)}
                        maxLength="50"
                        help={st('Manage/Fields/Name/Help')}
                        mandatory
                        readonly={!field.company_Id}
                    />
                    <Forms.Select
                        label={st('Manage/Fields/Entity/Label')}
                        data={entities}
                        value={field.crmType}
                        error={errors.crmType}
                        onChange={x => setValue('crmType', x)}
                        help={st('Manage/Fields/Entity/Help')}
                        mandatory
                        readonly={!field.company_Id}
                    />
                    {!field.company_Id ? (
                        <Forms.Text
                            label={st('Manage/Fields/Property/Label')}
                            value={field.query}
                            error={errors.query}
                            placeholder={st('Manage/Fields/Property/Placeholder')}
                            help={st('Manage/Fields/Property/Help')}
                            mandatory
                            readonly={!field.company_Id}
                        />
                    ) : (
                        <Forms.Select
                            label={st('Manage/Fields/Property/Label')}
                            value={field.query}
                            error={errors.query}
                            onChange={x => {
                                setValue('query', x);
                            }}
                            placeholder={st('Manage/Fields/Property/Placeholder')}
                            help={st('Manage/Fields/Property/Help')}
                            mandatory
                            data={entityFields}
                            readonly={!field.company_Id}
                        />
                    )}
                    {field.crmType === 2 && (
                        <Forms.Buttons label={st('Manage/Fields/ApplyTo/Label')} help={st('Manage/Fields/ApplyTo/Help')}>
                            <button
                                onClick={() => setValue('filter_Type', '')}
                                className={`mr10 btn ${!field.filter_Type && 'btn-primary'}`}
                                disabled={!field.company_Id}
                            >
                                {st('Manage/Fields/ApplyTo/All')}
                            </button>
                            <button
                                onClick={() => setValue('filter_Type', 'call')}
                                className={`mr10 btn ${field.filter_Type === 'call' && 'btn-primary'}`}
                                disabled={!field.company_Id}
                            >
                                {st('Manage/Fields/ApplyTo/Calls')}
                            </button>
                            <button
                                onClick={() => setValue('filter_Type', 'email')}
                                className={`mr10 btn ${field.filter_Type === 'email' && 'btn-primary'}`}
                                disabled={!field.company_Id}
                            >
                                {st('Manage/Fields/ApplyTo/Emails')}
                            </button>
                        </Forms.Buttons>
                    )}
                    <Forms.Buttons label={st('Manage/Fields/ValueType/Label')} help={st('Manage/Fields/ValueType/Help')}>
                        <button
                            onClick={() => setValue('value_Type', '')}
                            className={`mr10 btn ${!field.value_Type && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-text"
                        >
                            {st('Manage/Fields/ValueType/Text')}
                        </button>
                        <button
                            onClick={() => setValue('value_Type', 'L')}
                            className={`mr10 btn ${field.value_Type === 'L' && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-user"
                        >
                            {st('Manage/Fields/ValueType/User')}
                        </button>
                        <button
                            onClick={() => setValue('value_Type', 'Q')}
                            className={`mr10 btn ${field.value_Type === 'Q' && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-query"
                        >
                            {st('Manage/Fields/ValueType/Query')}
                        </button>
                        <button
                            onClick={() => setValue('value_Type', 'A')}
                            className={`mr10 btn ${field.value_Type === 'A' && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-amount"
                        >
                            {st('Manage/Fields/ValueType/Amount')}
                        </button>
                        <button
                            onClick={() => setValue('value_Type', 'D')}
                            className={`mr10 btn ${field.value_Type === 'D' && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-date"
                        >
                            {st('Manage/Fields/ValueType/Date')}
                        </button>
                        <button
                            onClick={() => setValue('value_Type', 'E')}
                            className={`mr10 btn ${field.value_Type === 'E' && 'btn-primary'}`}
                            disabled={!field.company_Id}
                            data-testid="button-valuetype-email"
                        >
                            {st('Manage/Fields/ValueType/Email')}
                        </button>
                    </Forms.Buttons>

                    {field.value_Type === 'L' && (
                        <Forms.Textarea
                            label={st('Manage/Fields/Mapping/Label')}
                            value={list}
                            onChange={setList}
                            help={st('Manage/Fields/Mapping/Help')}
                            placeholder={st('Manage/Fields/Mapping/Placeholder')}
                            rows="6"
                        />
                    )}
                    {field.value_Type === 'Q' && (
                        <Forms.Text
                            label={st('Manage/Fields/QueryObject/Label')}
                            value={field.value_Lookup}
                            error={errors.value_Lookup}
                            onChange={x => {
                                setValue('value_Lookup', x);
                                api.checkLookup(x);
                            }}
                            maxLength="50"
                            placeholder={st('Manage/Fields/QueryObject/Placeholder')}
                            help={st('Manage/Fields/QueryObject/Help')}
                            mandatory
                            readonly={!field.company_Id}
                        />
                    )}
                    {!['A', 'D', 'E'].some(x => x === field.value_Type) && (
                        <Forms.Toggle
                            label={st('Manage/Fields/121/Label')}
                            value={field.show_Chart}
                            onChange={x => setValue('show_Chart', x)}
                            help={st('Manage/Fields/121/Help')}
                            readonly={!field.company_Id}
                        />
                    )}
                </Modal>

                {loading ? (
                    <Loading />
                ) : (
                    <Table
                        className="wrap-headers"
                        data={ArrayHelpers.getPage(sort(data), page, pageSize)}
                        columns={tableColumns}
                        options={options}
                        removeItem={api.remove}
                        sort={column => {
                            if (column === sorter) setDesc(!desc);
                            setSorter(column);
                        }}
                        goToPage={page => setPage(page)}
                    />
                )}
            </section>
            <DeleteWarning message={st('DeleteWarning')} show={showDelete} close={confirm => deleteItem(confirm)} />
        </div>
    );
};

export default SalesforceFields;
